import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import PopupTemplate from '@arcgis/core/PopupTemplate';
import MapView from '@arcgis/core/views/MapView';
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { HbFinding, HbMapShape, HbSectionDto } from "src/app/models/hb-section-dto";
import { ApiService } from "src/app/services/api.service";
import { LanguageService } from "src/app/services/language.service";
import { ControlType } from "../arcgis-map/constants/control-type.enum";
import { MapControlItem } from "../arcgis-map/constants/map-control-item.interface";
import { ARCGIS_MAP_LIFECYCLE } from "../arcgis-map/constants/map-life-cycle.interface";
import { MainMapService } from "../arcgis-map/services/main-map.service";
import {
    HB_LINE_WIDTH,
    HB_POINT_SIZE,
    HB_HIGHLIGHT_OUTLINE,
    HB_LEGEND,
    HB_SECTION_BOUNDARY_COLOR,
    extentArea,
    hbTypeColor,
} from "./hb-symbology";

// Keeps the map popup short enough not to need scrolling; tune this to resize the photo.
const POPUP_IMAGE_MAX_HEIGHT = '120px';

@Component({
    selector: 'app-hb-section',
    templateUrl: './hb-section.component.html',
    styleUrls: ['./hb-section.component.scss'],
    providers: [
        {
            provide: ARCGIS_MAP_LIFECYCLE,
            useClass: MainMapService,
        },
    ],
})
export class HbSectionComponent implements OnInit, OnDestroy {
    closeClicked: Subject<void> = new Subject<void>();
    switchToMapTab: Subject<void> = new Subject<void>();
    currentLanguage$!: Observable<string>;

    sectionDetails!: HbSectionDto;
    sectionDetailsLoading = true;
    entityId = 0;
    // Both narrowings are on by default; turning them off widens to every imported finding.
    inReportOnly = true;
    withPhotosOnly = true;
    selectedEntityId: number | null = null;

    readonly legend = HB_LEGEND.map(({ hbType, label }) => ({
        label,
        color: `rgb(${hbTypeColor(hbType).join(',')})`,
    }));

    shapes: any[] = [];
    zoomShapeIds: string[] = [];
    mapControls: MapControlItem[] = [
        { position: 'top-left', type: ControlType.FullScreen },
        { position: 'top-left', type: ControlType.Zoom },
        {
            position: 'top-left',
            type: ControlType.BasemapGallery,
            expand: {
                id: 'basemap-gallery-expand',
                position: 'top-left',
                expandIcon: 'basemap',
                expandTooltip: `${this.translate.instant('iaa.map.expand-basemap-gallery')}`,
                mode: 'floating',
            },
        },
        {
            position: 'top-left',
            type: ControlType.Locate,
            attributes: { scale: 5000 },
        },
    ];

    private queryParamsSubscription?: Subscription;
    private legendObserver?: ResizeObserver;

    constructor(
        private api: ApiService,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private languageService: LanguageService,
    ) {}

    ngOnInit(): void {
        this.currentLanguage$ = this.languageService.currentLanguage$;

        this.queryParamsSubscription = this.route.queryParamMap.subscribe((params) => {
            const entityId = Number(params.get('entityId')) || 0;
            if (!entityId || entityId === this.entityId) {
                return;
            }
            this.entityId = entityId;
            this.getSectionDetails(entityId);
        });
    }

    ngOnDestroy(): void {
        this.legendObserver?.disconnect();
        this.queryParamsSubscription?.unsubscribe();
        this.switchToMapTab.complete();
    }

    getDetailsTitle(): string {
        const title = this.translate.instant('iaa.app.hbSection.title');
        const name = this.sectionDetails?.sectionTitle?.hebrew?.trim();
        if (!name) {
            return title;
        }
        // Some reports are already named "חתכי בדיקה ..."; don't say it twice.
        return name.startsWith(title)
            ? name
            : this.translate.instant('iaa.app.hbSection.pageTitle', { name });
    }

    // The archaeologist excluded a finding from the report with yazam_include_pic = 0;
    // it is still indexed for search, so the toggle can bring it back.
    get visibleFindings(): HbFinding[] {
        return (this.sectionDetails?.findings || []).filter((finding) =>
            (!this.inReportOnly || finding.yazamIncludePic !== 0)
            && (!this.withPhotosOnly || finding.images.length > 0));
    }

    setInReportOnly(inReportOnly: boolean): void {
        this.inReportOnly = inReportOnly;
        // The map shows the same findings as the table.
        this.buildShapes();
    }

    setWithPhotosOnly(withPhotosOnly: boolean): void {
        this.withPhotosOnly = withPhotosOnly;
        this.buildShapes();
    }

    // Clicking a row's map icon selects its shape; clicking it again clears the selection.
    highlightFinding(entityId: number): void {
        // On a small screen the map is a separate tab, so bring it into view.
        this.switchToMapTab.next();
        this.selectedEntityId = this.selectedEntityId === entityId ? null : entityId;
        this.buildShapes();
    }

    // Tells the map the legend covers its top, so popups, controls and zoom-to-extent all
    // stay below it; re-measured because the legend wraps to more rows on a narrow map.
    reserveLegendSpace(view: MapView, legend: HTMLElement): void {
        this.legendObserver?.disconnect();
        this.legendObserver = new ResizeObserver(() => {
            view.padding = { ...view.padding, top: legend.offsetTop + legend.offsetHeight };
        });
        this.legendObserver.observe(legend);
    }

    // The דוח ליזם has no entity page - the excavation page opens its PDF the same way.
    openYazamReport(): void {
        const url = this.sectionDetails?.reportUrl;
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }

    private getSectionDetails(entityId: number): void {
        this.sectionDetailsLoading = true;
        this.api
            .getHbSectionDetails(entityId)
            .pipe(takeUntil(this.closeClicked))
            .subscribe({
                next: (data: HbSectionDto) => {
                    this.sectionDetails = data;
                    this.buildShapes();
                    this.sectionDetailsLoading = false;
                },
                error: () => {
                    this.sectionDetailsLoading = false;
                },
            });
    }

    private buildShapes(): void {
        const mapInfo = this.sectionDetails?.mapInformation;
        const boundary = mapInfo?.sectionShape ? [this.convertBoundary(mapInfo.sectionShape)] : [];
        const findingByEntity = new Map<number, HbFinding>(
            this.visibleFindings.map((finding) => [finding.entityId, finding]));

        // Largest first, so a small finding inside a big one stays on top and clickable.
        const findings = (mapInfo?.findingShapes || [])
            .filter((shape) => findingByEntity.has(shape.shapeData.entity.entityId))
            .map((shape) => ({ shape, extent: extentArea(shape.shapeWkt) }))
            .sort((a, b) => b.extent - a.extent)
            .map(({ shape }) => this.convertFinding(shape, findingByEntity.get(shape.shapeData.entity.entityId)));

        this.shapes = [...boundary, ...findings];
        this.zoomShapeIds = boundary.map((shape) => shape.shapeId);
    }

    private convertBoundary(shape: HbMapShape): any {
        return {
            ...this.baseShape(shape),
            popupTemplate: this.createSectionPopup(),
            styledParams: {
                fillColor: [0, 0, 0, 0],
                borderColor: HB_SECTION_BOUNDARY_COLOR,
                borderWidth: 2,
            },
        };
    }

    // Points are filled with the class colour; lines and polygons carry it on the
    // outline, polygons with no fill at all, the way the ArcGIS renderer draws them.
    private convertFinding(shape: HbMapShape, finding: HbFinding | undefined): any {
        const color = hbTypeColor(finding?.hbType ?? null);
        const isPoint = finding?.geometryType === 'point';
        const isHighlighted = !!finding && this.selectedEntityId === finding.entityId;
        const base = this.baseShape(shape);
        return {
            ...base,
            shapeData: { ...base.shapeData, finding },
            popupTemplate: this.createFindingPopup(),
            highlighted: isHighlighted,
            styledParams: {
                fillColor: isPoint ? color : [...color, 0],
                borderColor: color,
                borderWidth: isHighlighted ? HB_LINE_WIDTH * 2 : HB_LINE_WIDTH,
                markerSize: isHighlighted ? HB_POINT_SIZE * 1.5 : HB_POINT_SIZE,
                markerOutlineColor: isHighlighted ? HB_HIGHLIGHT_OUTLINE : [0, 0, 0],
                markerOutlineWidth: isHighlighted ? 3 : 1,
            },
        };
    }

    private createSectionPopup(): PopupTemplate {
        const t = (key: string) => this.translate.instant(`iaa.app.hbSection.${key}`);
        return new PopupTemplate({
            title: () => this.sectionDetails?.sectionTitle?.hebrew || t('title'),
            content: () => {
                const details = this.sectionDetails;
                return this.popupRows([
                    [t('projectNumber'), details?.projectNumber],
                    [t('archaeologist'), details?.archaeologist],
                    [t('merchav'), details?.merchav],
                    [t('executionDates'), details?.executionDates],
                    // The section's displayable findings; a popup already open would not
                    // re-render, so this must not depend on the current filter.
                    [t('findings'), details?.findingsCount],
                ]);
            },
        });
    }

    private createFindingPopup(): PopupTemplate {
        const t = (key: string) => this.translate.instant(`iaa.app.hbSection.${key}`);
        const findingOf = (feature: any): HbFinding | undefined =>
            feature?.graphic?.attributes?.shape?.shapeData?.finding;

        return new PopupTemplate({
            title: (feature: any) => {
                const finding = findingOf(feature);
                if (!finding) {
                    return '';
                }
                const number = finding.snumber !== null ? ` ${finding.snumber}` : '';
                return `${finding.hbTypeName || t('findings')}${number}`;
            },
            content: (feature: any) => {
                const finding = findingOf(feature);
                if (!finding) {
                    return '';
                }
                const image = finding.images[0];
                return this.popupRows([
                    [t('snumber'), finding.snumber],
                    [t('geometryType'), this.translate.instant(
                        `iaa.app.hbSection.geometry.${finding.geometryType}`)],
                    [t('remarks'), finding.remarks],
                    [t('element'), finding.element],
                    [t('period'), finding.period],
                    [t('depth'), finding.depth],
                ]) + (image
                    ? `<img src="${image.mediaUrl}" alt="" style="max-width:100%;max-height:${POPUP_IMAGE_MAX_HEIGHT};height:auto;display:block;margin-top:10px;" />`
                    : '');
            },
        });
    }

    private popupRows(rows: [string, unknown][]): string {
        const filled = rows
            .filter(([, value]) => value !== null && value !== undefined && `${value}`.trim() !== '')
            .map(([label, value]) => `<div><strong>${label}:</strong> ${value}</div>`)
            .join('');
        return `<div id="popup" dir="rtl">${filled}</div>`;
    }

    private baseShape(shape: HbMapShape): any {
        return {
            shapeId: shape.shapeId.toString(),
            wkt: shape.shapeWkt,
            layerName: "שכבת תוצאות שאילתה",
            hidden: false,
            isExtent: true,
            shapeData: {
                entity: {
                    entityId: shape.shapeData.entity.entityId,
                    entityType: shape.shapeData.entity.entityType,
                },
            },
        };
    }
}
